import "babelify/polyfill";
import Interval from "./interval";
import Polygons from "./polygons";
import Grid from "./grid";

// This is clearly a terrible thing.
function traverse(decomp, f) {
  function clear(node) {
    if (!node.visited) {
      return;
    }
    node.visited = false;

    for (let child of node.children) {
      clear(child);
    }
  }

  for (let root of decomp) {
    f(root);
  }
  for (let root of decomp) {
    clear(root);
  }
}


function isAxial(node, prevSpan) {
  return true;
}

function isMaximal(node, prevSpan) {
  let shrinkAhead = (
    node.children.length !== 1
    || node.span[0] < node.children[0].span[0]
    || node.children[0].span[1] < node.span[1]
  );
  let shrinkBehind = (node.span[0] < prevSpan[0] || prevSpan[1] < node.span[1]);

  return shrinkAhead && shrinkBehind;
}

function findGuards(predicate, list, root) {
  (function findGuardsInner(node, prevSpan) {
    if (node.visited) {
      return;
    }
    node.visited = true;

    if (predicate(node, prevSpan)) {
      list.push({
        position: (node.width[0] + node.width[1])/2,
        span: node.span,
      });
    }

    for (let child of node.children) {
      findGuardsInner(child, node.span);
    }
  })(root, [0, 0]);
}

function execEach(f) {
  return function execEachInner(node) {
    if (node.visited) {
      return;
    }
    node.visited = true;

    f(node);

    for (let child of node.children) {
      execEachInner(child);
    }
  };
}

class Sim {
  constructor() {
    this.grid = new Grid(25, 25);
    this.guardMode = "maximal";
    this.edgeComparator = new EdgeComparator();

    this.x_edges = [];
    this.y_edges = [];
    this.x_decomp = [];
    this.y_decomp = [];
    this.x_guards = [];
    this.y_guards = [];
  }

  update() {
    // Identify and sort the polygon's edges
    let {x_edges, y_edges} = gridToEdges(this.grid, this.edgeComparator.compare);
    let x_decomp = findDecomposition(x_edges);
    let y_decomp = findDecomposition(y_edges);

    let x_guards = [];
    let y_guards = [];
    if (this.guardMode === "axial") {
      traverse(x_decomp, (root) => findGuards(isAxial, x_guards, root));
      traverse(y_decomp, (root) => findGuards(isAxial, y_guards, root));
    } else if (this.guardMode === "maximal") {
      traverse(x_decomp, (root) => findGuards(isMaximal, x_guards, root));
      traverse(y_decomp, (root) => findGuards(isMaximal, y_guards, root));
    }

    this.x_edges = x_edges;
    this.y_edges = y_edges;
    this.x_decomp = x_decomp;
    this.y_decomp = y_decomp;
    this.x_guards = x_guards;
    this.y_guards = y_guards;
  }
}

class EdgeComparator {
  constructor() {
    this.type = "open";

    this.compare = (e1, e2) => {
      if (e1.position < e2.position) {
        return -1;
      } else if (e1.position > e2.position) {
        return 1;
      } else if (this.shapeValue(e1.shape) < this.shapeValue(e2.shape)) {
        return -1;
      } else if (this.shapeValue(e1.shape) > this.shapeValue(e2.shape)) {
        return 1;
      } else if (e1.span[1] <= e2.span[0]) {
        return -1;
      } else if (e1.span[0] >= e2.span[1]) {
        return 1;
      } else {
        return 0;
      }
    };
  }

  shapeValue(shape) {
    let factor = (this.type === "open") ? 1 : -1;
    switch (shape) {
      case "left":   return -1*factor;
      case "right":  return 1*factor;
      default:       return 0;
    }
  }
}

function sortEdges(edges, comparator) {
  // Ensure that the edges are sorted along both axes
  edges.sort(comparator);

  // Decorate each edge with a "shape" in order to partition edges on the same scan-line
  let opens = [];
  for (let edge of edges) {
    for (let j = opens.length - 1; j >= 0; j -= 1) {
      let open = opens[j];
      if (open[0] === edge.span[0] && edge.span[1] === open[1]) {
        opens.splice(j, 1);
        edge.shape = "right";
        break;
      } else if (open[0] === edge.span[0]) {
        open[0] = edge.span[1];
        edge.shape = "right";
        break;
      } else if (edge.span[1] === open[1]) {
        open[1] = edge.span[0];
        edge.shape = "right";
        break;
      } else if (open[1] === edge.span[0]) {
        open[1] = edge.span[1];
        edge.shape = "left";
        break;
      } else if (edge.span[1] === open[0]) {
        if (j !== 0 && edge.span[0] === opens[j-1][1]) {
          opens.splice(j-1, 2, [opens[j-1][0], open[1]]);
        } else {
          open[0] = edge.span[0];
        }
        edge.shape = "left";
        break;
      } else if (open[0] < edge.span[0] && edge.span[1] < open[1]) {
        opens.splice(j, 1, [open[0], edge.span[0]], [edge.span[1], open[1]]);
        edge.shape = "right";
        break;
      } else if (edge.span[0] > open[1]) {
        edge.shape = "left";
        opens.splice(j+1, 0, [edge.span[0], edge.span[1]]);
        break;
      }
    }
    if (!edge.shape) {
      edge.shape = "left";
      opens.splice(0, 0, [edge.span[0], edge.span[1]]);
    }
  }
  // ASSERT: opens.length === 0

  // Sort the edges using the new information
  edges.sort(comparator);

  return edges;
}

function partitionEdges(polygon, comparator) {
  let x_edges = [];
  let y_edges = [];

  // Accumulate edges into y_edges and x_edges
  for (let shell of polygon.shells) {
    for (let i = 0; i < shell.length; i += 1) {
      let vertex = shell[i];
      let next = shell[(i+1)%shell.length];

      if (vertex[0] == next[0]) {
      // Vertical edge
        y_edges.push({
          position: vertex[0],
          span: [Math.min(vertex[1], next[1]), Math.max(vertex[1], next[1])],
        });
      } else {
      // Horizontal edge
        x_edges.push({
          position: vertex[1],
          span: [Math.min(vertex[0], next[0]), Math.max(vertex[0], next[0])],
        });
      }
    }
  }

  // Sort and return the edges
  return {
    y_edges: sortEdges(y_edges, comparator),
    x_edges: sortEdges(x_edges, comparator),
  };
}

function findDecomposition(edges) {
  let sweep_line = new Interval();

  for (let edge of edges) {
    // console.log(JSON.stringify(edge)+" -> "+JSON.stringify(sweep_line.spans));
    switch (edge.shape) {
    case "left": { // expansion
      sweep_line.insert(edge.span, edge.position);
    } break;
    case "right": { // contraction
      sweep_line.remove(edge.span, edge.position);
    } break;
    }
  }

  return sweep_line.roots;
}

function polygonToGrid(polygon, comparator) {
  let {y_edges} = partitionEdges(polygon, comparator);
  let y_decomp = findDecomposition(y_edges);

  let grid = new Grid(25, 25);

  // Convert the polygon to a grid
  function buildGrid(grid, tile) {
    for (let y = tile.span[0]; y < tile.span[1]; y += 1) {
      for (let x = tile.width[0]; x < tile.width[1]; x += 1) {
        grid.set(x, y, true);
      }
    }
    for (let child of tile.children) {
      buildGrid(grid, child);
    }
  }
  traverse(y_decomp, (root) => buildGrid(grid, root));

  return grid;
}

function gridToEdges(grid, comparator) {
  let y_edges = [];
  let x_edges = [];

  for (let x = 0; x <= 25; x += 1) {
    let open = null;
    for (let y = 0; y <= 25; y += 1) {
      if (open) {
        if (grid.get(x, y) !== grid.get(x, y-1) || grid.get(x-1, y) !== grid.get(x-1, y-1)) {
          y_edges.push({
            position: x,
            span: [open[1], y],
            shape: grid.get(x, y-1) ? "left" : "right",
          });
          open = null;
        }
      }

      if (!open) {
        if (grid.get(x, y) !== grid.get(x-1, y)) {
          open = [x, y];
        }
      }
    }
  }

  for (let y = 0; y <= 25; y += 1) {
    let open = null;
    for (let x = 0; x <= 25; x += 1) {
      if (open) {
        if (grid.get(x, y) !== grid.get(x-1, y) || grid.get(x, y-1) !== grid.get(x-1, y-1)) {
          x_edges.push({
            position: y,
            span: [open[0], x],
            shape: grid.get(x-1, y) ? "left" : "right",
          });
          open = null;
        }
      }

      if (!open) {
        if (grid.get(x, y) !== grid.get(x, y-1)) {
          open = [x, y];
        }
      }
    }
  }

  return {
    x_edges: x_edges.sort(comparator),
    y_edges: y_edges.sort(comparator),
  };
}

function edgesToPolygon(y_edges) {
  let shells = [];

  let open_chains = [];
  let dummyEdge = {position: 1/0, span: [1/0, 1/0]};
  for (let edge of y_edges) {
    let lchain = null;
    let rchain = null;

    // Does this edge close a chain?
    for (let i = open_chains.length - 1; i >= 0; i -= 1) {
      let chain = open_chains[i];

      // Does this edge attach to an existing chain?
      if (edge.span[0] === chain[0][1] && edge.position !== chain[0][0]) {
        lchain = ({chain, i});
      } else if (edge.span[0] === chain[chain.length-1][1] && edge.position !== chain[chain.length-1][0]) {
        lchain = ({chain, i});
      }

      if (edge.span[1] === chain[0][1] && edge.position !== chain[0][0]) {
        rchain = ({chain, i});
      } else if (edge.span[1] === chain[chain.length-1][1] && edge.position !== chain[chain.length-1][0]) {
        rchain = ({chain, i});
      }
    }

    if (lchain === null && rchain === null) {
    // No chain was hit - add the edge to a new one
      open_chains.push([[edge.position, edge.span[0]], [edge.position, edge.span[1]]]);
      continue;
    } else if (lchain === null) {
      if (rchain.chain[0][1] === edge.span[1]) {
        rchain.chain.splice(0, 0, [edge.position, edge.span[0]], [edge.position, edge.span[1]]);
      } else {
        rchain.chain.push([edge.position, edge.span[1]], [edge.position, edge.span[0]]);
      }
    } else if (rchain === null) {
      if (lchain.chain[0][1] === edge.span[0]) {
        lchain.chain.splice(0, 0, [edge.position, edge.span[1]], [edge.position, edge.span[0]]);
      } else {
        lchain.chain.push([edge.position, edge.span[0]], [edge.position, edge.span[1]]);
      }
    } else if (lchain.i === rchain.i) {
    // A chain was completed
      if (lchain.chain[0][1] === edge.span[0]) {
        lchain.chain.splice(0, 0, [edge.position, edge.span[1]], [edge.position, edge.span[0]]);
      } else {
        lchain.chain.push([edge.position, edge.span[0]], [edge.position, edge.span[1]]);
      }
      open_chains.splice(lchain.i, 1);
      shells.push(lchain.chain);
      continue;
    } else {
      open_chains.splice(rchain.i, 1);
      if (lchain.chain[lchain.chain.length-1][1] === edge.span[0] && rchain.chain[0][1] === edge.span[1]) {
      // Joining two chains by opposing sides
        lchain.chain.push([edge.position, edge.span[0]], [edge.position, edge.span[1]]);
        for (let j = 0; j < rchain.chain.length; j += 1) {
          lchain.chain.push(rchain.chain[j]);
        }
      } else if (lchain.chain[lchain.chain.length-1][1] === edge.span[0] && rchain.chain[rchain.chain.length-1][1] === edge.span[1]) {
      // Joining two chains on the right
        lchain.chain.push([edge.position, edge.span[0]], [edge.position, edge.span[1]]);
        for (let j = rchain.chain.length - 1; j >= 0; j -= 1) {
          lchain.chain.push(rchain.chain[j]);
        }
      } else if (lchain.chain[0][1] === edge.span[0] && rchain.chain[0][1] === edge.span[1]) {
      // Joining two chains on the left... ugh
        lchain.chain.splice(0, 0, [edge.position, edge.span[1]], [edge.position, edge.span[0]]);
        for (let j = 0; j < rchain.chain.length; j += 1) {
          lchain.chain.splice(0, 0, rchain.chain[j]);
        }
      }
    }
  }

  // ASSERT: open_chains.length === 0

  return shells;
}

class CavnvasRenderer {
  constructor(ctx) {
    this.ctx = ctx;
  }

  clear(color) {
    this.ctx.save();
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.fillStyle = color || "#FFFFFF";
    this.ctx.fillRect(0, 0, this.ctx.canvas.clientWidth, this.ctx.canvas.clientHeight);
    this.ctx.restore();
  }

  drawLine(p1, p2, color, width) {
    this.ctx.save();
    this.ctx.beginPath();
    this.ctx.moveTo(p1[0], p1[1]);
    this.ctx.lineTo(p2[0], p2[1]);
    this.ctx.strokeStyle = color || "#000000";
    this.ctx.lineWidth *= width || 1;
    this.ctx.stroke();
    this.ctx.restore();
  }

  drawTile(p1, p2, color, alpha) {
    this.ctx.save();
    this.ctx.globalAlpha = alpha;
    this.ctx.fillStyle = color;
    this.ctx.fillRect(p1[0], p1[1], p2[0]-p1[0], p2[1]-p1[1]);
    this.ctx.restore();
  }
}

class TikzRenderer {
  constructor() {
    this.commands = [];
  }

  clear(color) {
    // Do nothing
  }

  drawLine(p1, p2, color, width) {
    color = color || "#000000";
    this.commands.push("\\definecolor{tmp}{HTML}{"+color.substr(1)+"}");
    this.commands.push("\\draw[color=tmp] ("+p1[0]+","+p1[1]+") -- ("+p2[0]+","+p2[1]+");");
  }

  drawTile(p1, p2, color, alpha) {
    // Do nothing
    color = color || "#000000";
    this.commands.push("\\definecolor{tmp}{HTML}{"+color.substr(1)+"}");
    this.commands.push("\\draw[fill,color=tmp] ("+p1[0]+","+p1[1]+") -- ("+p1[0]+","+p2[1]+") -- ("+p2[0]+","+p2[1]+") -- ("+p2[0]+","+p1[1]+");");
  }

  compile() {
    return this.commands.join("\n");
  }
}

class StandardView {
  constructor() {
    this.drawVerticalWalls = true;
    this.drawHorizontalWalls = true;
    this.drawVerticalGuards = true;
    this.drawHorizontalGuards = true;
    this.drawTiles = false;
  }

  render(sim, r) {
    // Fill in the polygon's interior
    for (let x = 0; x < 25; x += 1) {
      for (let y = 0; y < 25; y += 1) {
        if (sim.grid.get(x, y)) {
          r.drawTile([x, y], [x+1+1/20, y+1+1/20], "#FFFFFF");
        }
      }
    }

    // Render the various layers of the polygon
    if (this.drawVerticalWalls) {
      for (let edge of sim.y_edges) {
        r.drawLine([edge.position, edge.span[0]], [edge.position, edge.span[1]], "#000000");
      }
    }
    if (this.drawHorizontalWalls) {
      for (let edge of sim.x_edges) {
        r.drawLine([edge.span[0], edge.position], [edge.span[1], edge.position], "#000000");
      }
    }
    if (this.drawVerticalGuards) {
      for (let guard of sim.y_guards) {
        r.drawLine([guard.position, guard.span[0]], [guard.position, guard.span[1]], "#00DD22", 2);
      }
    }
    if (this.drawHorizontalGuards) {
      for (let guard of sim.x_guards) {
        r.drawLine([guard.span[0], guard.position], [guard.span[1], guard.position], "#0022FF", 2);
      }
    }

    if (this.drawTiles) {
      traverse(sim.x_decomp, execEach((rect) => {
        r.drawTile([rect.span[0]+0.2, rect.width[0]+0.2], [rect.span[1]-0.2, rect.width[1]-0.2], "#FFAA00", 0.3);
      }));
      traverse(sim.y_decomp, execEach((rect) => {
        r.drawTile([rect.width[0]+0.2, rect.span[0]+0.2], [rect.width[1]-0.2, rect.span[1]-0.2], "#FFAA00", 0.3);
      }));
    }
  }
}

function refreshStatistics(sim) {
  document.getElementById("vertex-count").textContent = sim.y_edges.length + sim.x_edges.length;
  document.getElementById("guard-count").textContent = sim.x_guards.length + sim.y_guards.length;
  document.getElementById("horizontal-guard-count").textContent = sim.x_guards.length;
  document.getElementById("vertical-guard-count").textContent = sim.y_guards.length;
}

function rerender(sim, view, ctx) {
  let r = new CavnvasRenderer(ctx);
  r.clear("#AAAAAA");
  view.render(sim, r);
}

function buildTikz(sim, view) {
  let r = new TikzRenderer();
  view.render(sim, r);
  return r.compile();
}


let canvas = document.getElementById("canvas");
let ctx = canvas.getContext("2d");
ctx.transform(1, 0, 0, -1, 0, ctx.canvas.clientHeight);  // Flip the Y-axis
ctx.transform(1, 0, 0, 1, 0.5, 0.5);  // Align lines with pixels
ctx.transform(20, 0, 0, 20, 0, 0);  // Scale all distances,
ctx.lineWidth = 1/20;  // but keep lines the same width

// Initialize a new view
let standardView = new StandardView();
standardView.drawVerticalWalls = true;
standardView.drawHorizontalWalls = true;
standardView.drawVerticalGuards = document.querySelector("input[name='draw-vertical-guards']").checked;
standardView.drawHorizontalGuards = document.querySelector("input[name='draw-horizontal-guards']").checked;
standardView.drawTiles = document.querySelector("input[name='draw-tiles']").checked;

// Initialize the simulation
let sim = new Sim();
sim.edgeComparator.type = document.querySelector("input[name='collinearity-selector']:checked").value;
sim.guardMode = document.querySelector("input[name='guard-selector']:checked").value;
sim.grid = polygonToGrid(Polygons[document.getElementById("polygon-selector").value], sim.edgeComparator.compare);


// Bind all of the UI events we need to respond to
document.getElementById("polygon-selector").onchange = function(ev) {
  sim.grid = polygonToGrid(Polygons[ev.target.value], sim.edgeComparator.compare);
  sim.update();
  refreshStatistics(sim);
  rerender(sim, standardView, ctx);
};

var els = document.querySelectorAll(".render-toggle");
for (let i = 0; i < els.length; i += 1) {
  els[i].onchange = function(ev) {
    standardView[ev.target.value] = ev.target.checked;
    rerender(sim, standardView, ctx);
  };
};

var els = document.querySelectorAll("input[name='guard-selector']");
for (let i = 0; i < els.length; i += 1) {
  els[i].onchange = function(ev) {
    sim.guardMode = ev.target.value;
    sim.update();
    refreshStatistics(sim);
    rerender(sim, standardView, ctx);
  };
}

var els = document.querySelectorAll("input[name='collinearity-selector']");
for (let i = 0; i < els.length; i += 1) {
  els[i].onchange = function(ev) {
    sim.edgeComparator.type = ev.target.value;
    sim.update();
    refreshStatistics(sim);
    rerender(sim, standardView, ctx);
  };
}

canvas.onmousedown = function(ev) {
  ev.preventDefault();
  let offsetX = Math.max(0, Math.min(ev.offsetX, 499));
  let offsetY = Math.max(0, Math.min(ev.offsetY, 499));
  let x = offsetX/20 |0;
  let y = 25 - offsetY/20 |0;

  let value = !sim.grid.get(x, y);
  sim.grid.set(x, y, value);
  sim.update();
  refreshStatistics(sim);
  rerender(sim, standardView, ctx);

  let lastX = x;
  let lastY = y;

  canvas.onmousemove = function(ev) {
    ev.preventDefault();
    if (ev.offsetX < 0 || 499 < ev.offsetX
    || ev.offsetY < 0 || 499 < ev.offsetY) {
      return;
    }

    let offsetX = Math.max(0, Math.min(ev.offsetX, 499));
    let offsetY = Math.max(0, Math.min(ev.offsetY, 499));
    let x = offsetX/20 |0;
    let y = 25 - offsetY/20 |0;

    if (x === lastX && y === lastY) {
      return;
    }
    lastX = x;
    lastY = y;

    sim.grid.set(x, y, value);
    sim.update();
    refreshStatistics(sim);
    rerender(sim, standardView, ctx);
  };
};
canvas.onmouseup = function(ev) {
  ev.preventDefault();
  canvas.onmousemove = undefined;
};

// Update and render the simulation for the first time
sim.update();
refreshStatistics(sim);
rerender(sim, standardView, ctx);
